import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Fuse from "fuse.js";
import { fetchAllSections, SEARCH_LANGUAGES } from "../../services/SiteSearchData";
import { getImage } from "../../services/ImageService";

const FUSE_OPTIONS = {
    keys: [
        { name: "title", weight: 3 },
        { name: "chapter", weight: 1 },
        { name: "description", weight: 1 },
    ],
    threshold: 0.35,
    minMatchCharLength: 2,
    // Descriptions can be long (whole paragraphs of markdown content). With
    // location-aware matching, Fuse penalises hits far from the start of the
    // field, so e.g. a name near the end of a long article scores poorly and
    // gets dropped. Disable location to score matches consistently regardless
    // of where they appear.
    ignoreLocation: true,
    // Needed to order the other language's hits among themselves.
    includeScore: true,
};

const MAX_RESULTS = 8;

// Hits from the language not being read are pushed down by this much before
// ranking. Fuse scores 0 for a perfect match and 1 for the worst, and anything
// above FUSE_OPTIONS.threshold is already dropped, so a small constant settles
// ties in favour of the language on screen without burying a clearly better
// match in the other one.
const OTHER_LANGUAGE_PENALTY = 0.05;

// Ranking alone is not enough to make the other language visible: a term like
// "forest" matches Swedish pages perfectly too, because every one of them
// mentions Global Forester, so the local hits fill every slot. Hold back a
// couple of slots for the other language — but only for hits close in quality
// to the best one found, so a search that is already well answered in the
// language on screen is not padded with weak matches from the other.
const OTHER_LANGUAGE_SLOTS = 2;
const OTHER_LANGUAGE_QUALITY_WINDOW = 0.15;

export function SiteSearch() {
    const { t } = useTranslation();
    const { i18n } = useTranslation();
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [activeIndex, setActiveIndex] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [isIndexReady, setIsIndexReady] = useState(false);
    const inputRef = useRef(null);
    const overlayRef = useRef(null);
    const fuses = useRef(null); // one Fuse index per language

    // Both languages are searched and the results ranked together, so a term
    // only used in one of them still surfaces: "Markfukt" read in English, or
    // "forest" read in Swedish, where every page mentions Global Forester and
    // the local hits would otherwise fill every slot.
    const runSearch = useCallback(
        (text) => {
            if (!fuses.current || text.length < 2) return [];
            const viewed = i18n.resolvedLanguage;
            // Viewed language first so an exact tie keeps its order.
            const languages = [viewed, ...SEARCH_LANGUAGES.filter((language) => language !== viewed)];
            const ranked = languages
                .flatMap((language) =>
                    (fuses.current[language]?.search(text) ?? []).map((hit) => ({
                        item: hit.item,
                        rank: hit.score + (language === viewed ? 0 : OTHER_LANGUAGE_PENALTY),
                    })),
                )
                .sort((a, b) => a.rank - b.rank);
            if (ranked.length === 0) return [];

            const cutoff = ranked[0].rank + OTHER_LANGUAGE_QUALITY_WINDOW;
            const reserved = ranked
                .filter((hit) => hit.item.language !== viewed && hit.rank <= cutoff)
                .slice(0, OTHER_LANGUAGE_SLOTS);
            const rest = ranked
                .filter((hit) => !reserved.includes(hit))
                .slice(0, MAX_RESULTS - reserved.length);

            return [...reserved, ...rest]
                .sort((a, b) => a.rank - b.rank)
                .map((hit) => hit.item);
        },
        [i18n.resolvedLanguage],
    );

    // Both languages are indexed, so the indexes do not depend on the language
    // being viewed — only the order of the results does.
    useEffect(() => {
        if (!isOpen || fuses.current) return;
        setIsLoading(true);
        Promise.all(
            SEARCH_LANGUAGES.map((language) =>
                fetchAllSections(language).then((sections) => [language, new Fuse(sections, FUSE_OPTIONS)]),
            ),
        ).then((indexes) => {
            fuses.current = Object.fromEntries(indexes);
            setIsLoading(false);
            setIsIndexReady(true);
        });
    }, [isOpen]);

    const open = useCallback(() => {
        setIsOpen(true);
        setQuery("");
        setResults([]);
        setActiveIndex(0);
    }, []);

    const close = useCallback(() => {
        setIsOpen(false);
        setQuery("");
        setResults([]);
    }, []);

    useEffect(() => {
        if (isOpen) {
            setTimeout(() => inputRef.current?.focus(), 50);
        }
    }, [isOpen]);

    // Re-runs when the index finishes loading as well, so a query typed while
    // it was still being built does not stay without results until the next
    // keystroke.
    useEffect(() => {
        setResults(runSearch(query));
        setActiveIndex(0);
    }, [query, runSearch, isIndexReady]);

    const navigate_to = useCallback(
        (result) => {
            close();
            if (result.language && result.language !== i18n.resolvedLanguage) {
                // The row was written in the other language and its anchor only
                // exists in that rendering, so switch before navigating. Mirrors
                // what LanguageSelection does.
                i18n.changeLanguage(result.language);
                localStorage.setItem("selectedLanguage", result.language);
            }
            const path = result.path;
            const hashIndex = path.indexOf("#");
            const hash = hashIndex >= 0 ? path.slice(hashIndex + 1) : "";
            navigate(path);
            if (!hash) return;
            // Manual scroll-to-hash is needed for both:
            //  - same-page hash: navigate() doesn't trigger scroll at all
            //  - cross-page hash: ScrollToTopOnRouteChange (App.js) resets
            //    scroll to 0 on pathname change, and the target element may
            //    not yet be rendered (lazy-loaded route chunks).
            // Poll for the target instead of guessing a delay.
            const startTime = Date.now();
            const tryScroll = () => {
                const el = document.getElementById(hash);
                if (el) {
                    el.scrollIntoView({ behavior: "smooth" });
                    return;
                }
                if (Date.now() - startTime < 2000) {
                    requestAnimationFrame(tryScroll);
                }
            };
            // Defer first attempt so route change & ScrollToTopOnRouteChange
            // run before we scroll.
            setTimeout(tryScroll, 50);
        },
        [close, navigate, i18n],
    );

    const handleKeyDown = (e) => {
        if (e.key === "Escape") {
            close();
        } else if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveIndex((i) => Math.min(i + 1, results.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIndex((i) => Math.max(i - 1, 0));
        } else if (e.key === "Enter" && results[activeIndex]) {
            navigate_to(results[activeIndex]);
        }
    };

    // Keyboard shortcut: Ctrl+K / Cmd+K
    useEffect(() => {
        const handleGlobal = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === "k") {
                e.preventDefault();
                isOpen ? close() : open();
            }
        };
        window.addEventListener("keydown", handleGlobal);
        return () => window.removeEventListener("keydown", handleGlobal);
    }, [isOpen, open, close]);

    // Custom event so other components can open the site search modal
    useEffect(() => {
        window.addEventListener("open-site-search", open);
        return () => window.removeEventListener("open-site-search", open);
    }, [open]);

    return (
        <>
            {isOpen &&
                createPortal(
                    <div
                        ref={overlayRef}
                        className="fixed inset-0 z-[100] flex items-start justify-center pt-32 px-4"
                        onClick={(e) => {
                            if (e.target === overlayRef.current) close();
                        }}
                        style={{ background: "rgba(0,0,0,0.4)" }}
                    >
                        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden">
                            <div className="flex items-center px-6 py-4">
                                <img
                                    className="pointer-events-none w-6 h-6 mr-4 opacity-50"
                                    src={getImage("gui/search-pine.svg")}
                                    alt=""
                                    draggable={false}
                                />
                                <input
                                    ref={inputRef}
                                    type="search"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    placeholder={t("search-placeholder")}
                                    className="flex-1 outline-none text-base bg-transparent"
                                    aria-label="Search site"
                                    aria-autocomplete="list"
                                    aria-controls="site-search-results"
                                />
                                <button
                                    onClick={close}
                                    className="ml-3 text-sm font-medium text-pine opacity-50"
                                    aria-label="Close search"
                                >
                                    ESC
                                </button>
                            </div>

                            {isLoading && (
                                <div className="px-6 py-8 text-center text-pine text-sm">
                                    {t("search-loading")}
                                </div>
                            )}

                            {!isLoading && results.length > 0 && (
                                <ul
                                    id="site-search-results"
                                    role="listbox"
                                    className="py-2 max-h-96 overflow-y-auto"
                                >
                                    {results.map((result, i) => (
                                        <li
                                            key={`${result.path}-${result.title}-${i}`}
                                            role="option"
                                            aria-selected={i === activeIndex}
                                            className={`px-6 py-3 cursor-pointer transition-colors ${
                                                i === activeIndex
                                                    ? "bg-[rgba(36,45,41,0.05)]"
                                                    : ""
                                            }`}
                                            onMouseEnter={() =>
                                                setActiveIndex(i)
                                            }
                                            onClick={() =>
                                                navigate_to(result)
                                            }
                                        >
                                            <div className="flex items-center gap-2">
                                                <span className="font-semibold text-pine text-sm min-w-0">
                                                    {result.title}
                                                </span>
                                                {result.language !==
                                                    i18n.resolvedLanguage && (
                                                    <span className="text-xs text-pine bg-[rgba(36,45,41,0.08)] rounded-full px-2 py-0.5 shrink-0">
                                                        {t(result.language)}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-xs text-pine opacity-50 mt-0.5">
                                                {result.chapter}
                                            </div>
                                            {result.description && (
                                                <div className="text-pine text-xs mt-0.5 line-clamp-1">
                                                    {result.description}
                                                </div>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            )}

                            {!isLoading &&
                                query.length >= 2 &&
                                results.length === 0 && (
                                    <div className="px-6 py-8 text-center text-pine text-sm">
                                        {t("search-no-results")} "{query}
                                        "
                                    </div>
                                )}
                        </div>
                    </div>,
                    document.body,
                )}
        </>
    );
}
